import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Stack,
    IconButton,
    Snackbar,
    Alert,
    TextField,
    Autocomplete,
    Grid,
    Paper,
    Tooltip,
    Button,
} from "@mui/material";
import moment from "moment";
import ViewIcon from "@mui/icons-material/Visibility";
import { Download } from "@mui/icons-material";

import { Link, useSearchParams } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, differenceInDays } from "date-fns";
import { useAppMeta, useAuth, useTablePagination } from "hooks";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";

import CustomTabs from "components/Shared/CustomTabs";
import BaseSelect from "components/Shared/Base/BaseSelect";

import {
    useLazyGetVendorRMWiseReportQuery,
    useDownloadVendorRMWiseReportExcelMutation,
} from "store/services/reportApi";
import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";


const VendorRMWiseReport = () => {
    const { viewEntity } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    // State management
    const [sortBy, setSortBy] = useState("shipmentCount:-1");
    const [relationshipManager, setRelationshipManager] = useState("");
    const [onboardedBy, setOnboardedBy] = useState("");
    const [timeRange, setTimeRange] = useState("today");
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    // These are the dates actually used for fetching — only updated on "Apply" for custom range
    const [appliedFromDate, setAppliedFromDate] = useState(null);
    const [appliedToDate, setAppliedToDate] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const activeTab = searchParams.get("tab") || "rm";

    // Clear filters when switching tabs
    useEffect(() => {
        setRelationshipManager("");
        setOnboardedBy("");
    }, [activeTab]);

    // Memoized configurations
    const tabs = useMemo(() => [
        { label: "Relationship Manager", value: "rm" },
        { label: "Onboarded By", value: "onboarded" },
    ], []);

    const sortOptions = useMemo(() => [
        { label: "Vendor Count (Desc)", value: "vendorCount:-1" },
        { label: "Vendor Count (Asc)", value: "vendorCount:1" },
        { label: "Shipment Count (Desc)", value: "shipmentCount:-1" },
        { label: "Shipment Count (Asc)", value: "shipmentCount:1" },
        { label: "Charge (Desc)", value: "charge:-1" },
        { label: "Charge (Asc)", value: "charge:1" },
    ], []);

    const timeRangeOptions = useMemo(() => [
        { label: "Today", value: "today" },
        { label: "Last 7 Days", value: "last7days" },
        { label: "Last Month", value: "lastMonth" },
        { label: "Custom", value: "custom" },
    ], []);

    // API queries
    const [
        fetchVendorRMWiseReport,
        {
            data: vendorReportData,
            isLoading: isVendorLoading,
            isFetching: isVendorFetching,
        },
    ] = useLazyGetVendorRMWiseReportQuery();

    const [downloadExcel] = useDownloadVendorRMWiseReportExcelMutation();

    const [
        fetchUsers,
        { data: usersData, isLoading: isUsersLoading },
    ] = useLazyGetDDLSystemUserListQuery();

    // Fetch users on component mount
    useEffect(() => {
        const accountId = viewEntity?.entity?.account?._id;
        const entityId = viewEntity?.entity?._id;

        if (accountId && entityId) {
            fetchUsers({ account: accountId, entity: entityId });
        }
    }, [fetchUsers, viewEntity?.entity?.account?._id, viewEntity?.entity?._id]);

    // Memoized user options
    const userOptions = useMemo(() => [
        { label: "All Users", value: "" },
        ...(usersData?.map((user) => ({
            label: user.name,
            value: user._id,
        })) || []),
    ], [usersData]);

    // Date range calculation
    const getDateRange = useCallback((range, from, to) => {
        const today = moment().startOf("day");

        switch (range) {
            case "today":
                return {
                    fromDate: today.format("MM-DD-YYYY"),
                    toDate: today.endOf("day").format("MM-DD-YYYY"),
                };
            case "last7days":
                return {
                    fromDate: moment()
                        .subtract(6, "days")
                        .startOf("day")
                        .format("MM-DD-YYYY"),
                    toDate: today.endOf("day").format("MM-DD-YYYY"),
                };
            case "lastMonth":
                return {
                    fromDate: moment()
                        .subtract(1, "month")
                        .startOf("month")
                        .format("MM-DD-YYYY"),
                    toDate: moment()
                        .subtract(1, "month")
                        .endOf("month")
                        .format("MM-DD-YYYY"),
                };
            case "custom":
                if (from && to && moment(from).isValid() && moment(to).isValid()) {
                    if (differenceInDays(new Date(to), new Date(from)) > 90) {
                        return { fromDate: null, toDate: null };
                    }
                    return {
                        fromDate: moment(from).format("MM-DD-YYYY"),
                        toDate: moment(to).format("MM-DD-YYYY"),
                    };
                }
                return { fromDate: null, toDate: null };
            default:
                return { fromDate: null, toDate: null };
        }
    }, []);

    // Fetch report data
    const fetchReportData = useCallback(() => {
        // For custom range, don't hit the API until both dates are applied via the Apply button
        if (timeRange === "custom" && (!appliedFromDate || !appliedToDate)) {
            return;
        }
        const [sortedBy, sortDirection] = sortBy.split(":");
        const { fromDate, toDate } = getDateRange(
            timeRange,
            appliedFromDate,
            appliedToDate
        );

        // Update URL params
        const newParams = new URLSearchParams(searchParams);

        // Only set the relevant filter based on active tab
        if (activeTab === "rm") {
            if (relationshipManager) {
                newParams.set("relationshipManager", relationshipManager);
            } else {
                newParams.delete("relationshipManager");
            }
            newParams.delete("onboardedBy"); // Clear the other filter
        } else {
            if (onboardedBy) {
                newParams.set("onboardedBy", onboardedBy);
            } else {
                newParams.delete("onboardedBy");
            }
            newParams.delete("relationshipManager"); // Clear the other filter
        }

        newParams.set("timeRange", timeRange);
        if (fromDate) newParams.set("fromDate", fromDate);
        else newParams.delete("fromDate");
        if (toDate) newParams.set("toDate", toDate);
        else newParams.delete("toDate");

        setSearchParams(newParams, { replace: true });

        // API parameters
        const apiParams = {
            page: currentPage,
            limit: perPage,
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
            fromDate,
            toDate,
            sortedBy,
            sortDirection: parseInt(sortDirection),
            groupBy: activeTab,
        };

        // Add the appropriate filter based on active tab
        if (activeTab === "rm" && relationshipManager) {
            apiParams.relationshipManager = relationshipManager;
        } else if (activeTab === "onboarded" && onboardedBy) {
            apiParams.onboardedBy = onboardedBy;
        }

        fetchVendorRMWiseReport({ params: apiParams }).catch((err) => {
            setErrorMessage(
                err?.data?.message || "Failed to fetch vendor RM wise report"
            );
        });
    }, [
        sortBy,
        getDateRange,
        timeRange,
        appliedFromDate,
        appliedToDate,
        searchParams,
        relationshipManager,
        onboardedBy,
        setSearchParams,
        currentPage,
        perPage,
        viewEntity?.entity?.account?._id,
        viewEntity?.entity?._id,
        fetchVendorRMWiseReport,
        activeTab,
    ]);

    // Effect to fetch data when dependencies change
    useEffect(() => {
        const accountId = viewEntity?.entity?.account?._id;
        const entityId = viewEntity?.entity?._id;

        if (accountId && entityId) {
            fetchReportData();
        }
    }, [fetchReportData, viewEntity?.entity?._id, viewEntity?.entity?.account?._id]);

    // Memoized table columns
    const columns = useMemo(() => [
        {
            name: activeTab === "rm" ? "Relationship Manager" : "Onboarded By",
            selector: (row) => row.groupName,
            cell: (row) => row.groupName || "N/A",
            sortable: false,
        },
        {
            name: "Vendor Count",
            selector: (row) => row.vendorCount,
            cell: (row) => row.vendorCount || 0,
            sortable: false,
        },
        {
            name: "Shipment Count",
            selector: (row) => row.shipmentCount,
            cell: (row) => row.shipmentCount || 0,
            sortable: false,
        },
        {
            name: "Charge",
            selector: (row) => row.charge,
            cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
            sortable: false,
        },
        {
            name: "Details",
            cell: (row) => {
                const { fromDate, toDate } = getDateRange(
                    timeRange,
                    appliedFromDate,
                    appliedToDate
                );
                const queryParams = new URLSearchParams({
                    groupBy: activeTab,
                    // "none" = vendors with no RM / Onboarded By assigned
                    groupId: row.groupId || "none",
                    groupName: row.groupName || "Unassigned",
                    timeRange,
                    ...(fromDate ? { fromDate } : {}),
                    ...(toDate ? { toDate } : {}),
                });
                return (
                    <Link
                        to={`/app/crm/reports/vendor-RM-report-detail?${queryParams.toString()}`}
                    >
                        <IconButton color="primary" size="small">
                            <ViewIcon />
                        </IconButton>
                    </Link>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ], [activeTab, timeRange, appliedFromDate, appliedToDate, getDateRange]);

    // Memoized data
    const { filteredData, summaryData } = useMemo(() => ({
        filteredData: vendorReportData?.data?.details || [],
        summaryData: vendorReportData?.data?.summary || {
            totalVendorCount: 0,
            totalShipmentCount: 0,
            totalCharge: 0,
            unassignedVendorCount: 0,
        },
    }), [vendorReportData]);

    // Range display helper
    const getRangeDisplay = useCallback(() => {
        switch (timeRange) {
            case "today":
                return "Today";
            case "last7days":
                return "Last 7 Days";
            case "lastMonth":
                return "Last Month";
            case "custom":
                if (customFromDate && customToDate) {
                    return `Custom (${moment(customFromDate).format(
                        "MM/DD/YYYY"
                    )} - ${moment(customToDate).format("MM/DD/YYYY")})`;
                }
                return "Custom (Invalid Dates)";
            default:
                return "All";
        }
    }, [timeRange, customFromDate, customToDate]);

    // Custom date change handlers
    const handleFromDateChange = useCallback((newValue) => {
        setCustomFromDate(newValue);
        if (customToDate && differenceInDays(customToDate, newValue) > 90) {
            setCustomToDate(addDays(newValue, 90));
            setErrorMessage("Date range adjusted to maximum 90 days.");
        }
    }, [customToDate]);

    const handleToDateChange = useCallback((newValue) => {
        setCustomToDate(newValue);
        if (customFromDate && differenceInDays(newValue, customFromDate) > 90) {
            setCustomFromDate(addDays(newValue, -90));
            setErrorMessage("Date range adjusted to maximum 90 days.");
        }
    }, [customFromDate]);

    // Handle time range change
    const handleTimeRangeChange = useCallback((e) => {
        const value = e.target.value;
        setTimeRange(value);
        if (value !== "custom") {
            setCustomFromDate(null);
            setCustomToDate(null);
            setAppliedFromDate(null);
            setAppliedToDate(null);
        }
    }, []);

    // Custom range is valid when both dates are set, ordered, and within 90 days
    const isCustomRangeValid =
        customFromDate &&
        customToDate &&
        moment(customFromDate).isValid() &&
        moment(customToDate).isValid() &&
        moment(customToDate).isSameOrAfter(moment(customFromDate), "day") &&
        differenceInDays(customToDate, customFromDate) <= 90;

    // Download the report as Excel with the applied date filters
    const handleDownloadExcel = async () => {
        const [sortedBy, sortDirection] = sortBy.split(":");
        const { fromDate, toDate } = getDateRange(
            timeRange,
            appliedFromDate,
            appliedToDate
        );

        const apiParams = {
            fromDate: !fromDate || fromDate === "null" ? undefined : fromDate,
            toDate: !toDate || toDate === "null" ? undefined : toDate,
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
            sortedBy,
            sortDirection,
            groupBy: activeTab,
            page: 1,
            limit: 10000,
        };

        // Add the appropriate filter based on active tab
        if (activeTab === "rm" && relationshipManager) {
            apiParams.relationshipManager = relationshipManager;
        } else if (activeTab === "onboarded" && onboardedBy) {
            apiParams.onboardedBy = onboardedBy;
        }

        try {
            const response = await downloadExcel({ params: apiParams }).unwrap();
            if (!response.blob) {
                throw new Error("No data returned from server");
            }

            const contentType =
                response.blob.type ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            if (!contentType.includes("spreadsheetml.sheet")) {
                throw new Error("Invalid content type received from server");
            }

            const url = window.URL.createObjectURL(response.blob);
            const a = document.createElement("a");
            a.href = url;
            const dateSuffix =
                fromDate && fromDate !== "null" && toDate && toDate !== "null"
                    ? `_From_${fromDate}_to_${toDate}`
                    : `_${moment().format("YYYYMMDD")}`;
            a.download = `Vendor_RM_Report${dateSuffix}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrorMessage(
                error?.data?.message ||
                "Failed to download Excel file. Check filters or server status."
            );
            console.error("Failed to download Excel:", error);
        }
    };

    const isLoading = isVendorLoading || isVendorFetching;
    const isDisabled = isUsersLoading || isLoading;

    const unassignedCount = summaryData.unassignedVendorCount || 0;
    // 4 columns when the Unassigned box is visible, otherwise the 3 summary boxes take a third each
    const summaryColSpan = unassignedCount > 0 ? 3 : 4;

    // Shared style so every summary box has the same height and alignment
    const summaryBoxSx = {
        p: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        height: "100%",
    };

    // Query params for the Unassigned vendors drill-down in the summary
    const { fromDate: unassignedFromDate, toDate: unassignedToDate } = getDateRange(
        timeRange,
        appliedFromDate,
        appliedToDate
    );
    const unassignedQuery = new URLSearchParams({
        groupBy: activeTab,
        groupId: "none",
        groupName: "Unassigned",
        timeRange,
        ...(unassignedFromDate ? { fromDate: unassignedFromDate } : {}),
        ...(unassignedToDate ? { toDate: unassignedToDate } : {}),
    });

    useAppMeta("Vendor RM Wise Report");

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <BreadcrumbLayout
                breadcrumbs={[
                    { title: "Reports", path: "/app/crm/reports/list" },
                    { title: "Vendor RM Wise Report" },
                ]}
            >
                <BreadcrumbLayout.Paper>
                    <CustomTabs tabs={tabs} activeTab={activeTab} />

                    <Box sx={{ display: "flex", flexDirection: "column", px: 2, pt: 2, pb: 1, gap: 2 }}>
                        {/* Header Row */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                            <Typography variant="h6">
                                Vendor Report by {activeTab === "rm" ? "Relationship Manager" : "Onboarded By"} - {getRangeDisplay()}
                            </Typography>

                            {/* Context-specific filters */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", justifyContent: "flex-end" }}>
                                {/* Show RM filter only on RM tab */}
                                {activeTab === "rm" && (
                                    <Autocomplete
                                        size="small"
                                        options={userOptions}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={userOptions.find((opt) => opt.value === relationshipManager) || { value: "", label: "All RM" }}
                                        onChange={(e, newValue) => setRelationshipManager(newValue?.value || "")}
                                        disableClearable
                                        disabled={isDisabled}
                                        sx={{ minWidth: 220 }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Relationship Manager" />
                                        )}
                                    />
                                )}

                                {/* Show Onboarded By filter only on onboarded tab */}
                                {activeTab === "onboarded" && (
                                    <Autocomplete
                                        size="small"
                                        options={userOptions}
                                        getOptionLabel={(option) => option.label}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        value={userOptions.find((opt) => opt.value === onboardedBy) || { value: "", label: "All Onboarded" }}
                                        onChange={(e, newValue) => setOnboardedBy(newValue?.value || "")}
                                        disableClearable
                                        disabled={isDisabled}
                                        sx={{ minWidth: 220 }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Onboarded By" />
                                        )}
                                    />
                                )}

                                {/* Range */}
                                <BaseSelect
                                    label="Range"
                                    value={timeRange}
                                    onChange={handleTimeRangeChange}
                                    options={timeRangeOptions}
                                    getOptionLabel={(opt) => opt.label}
                                    getOptionValue={(opt) => opt.value}
                                    size="small"
                                    sx={{ minWidth: 140 }}
                                />

                                {/* Custom Date Pickers */}
                                {timeRange === "custom" && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DatePicker
                                            label="From"
                                            value={customFromDate}
                                            onChange={handleFromDateChange}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" sx={{ width: 140 }} />
                                            )}
                                            maxDate={customToDate || new Date()}
                                            disableFuture
                                        />
                                        <DatePicker
                                            label="To"
                                            value={customToDate}
                                            onChange={handleToDateChange}
                                            renderInput={(params) => (
                                                <TextField {...params} size="small" sx={{ width: 140 }} />
                                            )}
                                            minDate={customFromDate}
                                            maxDate={new Date()}
                                            disableFuture
                                        />
                                        <Tooltip
                                            title={
                                                isCustomRangeValid
                                                    ? "Apply date range"
                                                    : "Select a valid range (max 90 days)"
                                            }
                                        >
                                            <span>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    disabled={!isCustomRangeValid}
                                                    onClick={() => {
                                                        setAppliedFromDate(customFromDate);
                                                        setAppliedToDate(customToDate);
                                                    }}
                                                >
                                                    Apply
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                )}

                                {/* Sort By */}
                                <BaseSelect
                                    label="Sort By"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    options={sortOptions}
                                    getOptionLabel={(opt) => opt.label}
                                    getOptionValue={(opt) => opt.value}
                                    size="small"
                                    sx={{ minWidth: 160 }}
                                />

                                {/* Download */}
                                <Tooltip title="Download Excel">
                                    <span>
                                        <IconButton
                                            onClick={handleDownloadExcel}
                                            color="primary"
                                            disabled={isLoading || !filteredData.length}
                                        >
                                            <Download />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Summary Section */}
                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={summaryColSpan}>
                                        <Box sx={summaryBoxSx}>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Vendors
                                            </Typography>
                                            <Typography variant="h6">
                                                {summaryData.totalVendorCount || 0}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={summaryColSpan}>
                                        <Box sx={summaryBoxSx}>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Shipments
                                            </Typography>
                                            <Typography variant="h6">
                                                {summaryData.totalShipmentCount || 0}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={summaryColSpan}>
                                        <Box sx={summaryBoxSx}>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Charge
                                            </Typography>
                                            <Typography variant="h6">
                                                Rs. {(summaryData.totalCharge || 0).toFixed(2)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    {unassignedCount > 0 && (
                                        <Grid item xs={12} sm={3}>
                                            <Link
                                                to={`/app/crm/reports/vendor-RM-report-detail?${unassignedQuery.toString()}`}
                                                style={{ textDecoration: "none", display: "block" }}
                                            >
                                                <Box
                                                    sx={{
                                                        ...summaryBoxSx,
                                                        borderColor: "primary.main",
                                                        cursor: "pointer",
                                                        "&:hover": { backgroundColor: "action.hover" },
                                                    }}
                                                >
                                                    <Typography variant="body2" color="primary">
                                                        Unassigned Vendors
                                                    </Typography>
                                                    <Typography variant="h6" color="primary" fontWeight={700}>
                                                        {unassignedCount}
                                                    </Typography>
                                                </Box>
                                            </Link>
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        )}
                    </Box>

                    {/* Table Section */}
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : filteredData.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                No data available for the selected filters. Try adjusting the
                                filters or check data in the database.
                            </Typography>
                        </Box>
                    ) : (
                        <AppTable
                            columns={columns}
                            data={filteredData}
                            progressPending={isLoading}
                            paginationServer
                            paginationPerPage={perPage}
                            paginationTotalRows={vendorReportData?.data?.totalItems ?? 0}
                            paginationDefaultPage={currentPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                        />
                    )}

                    <Snackbar
                        open={!!errorMessage}
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage("")}
                    >
                        <Alert severity="error" onClose={() => setErrorMessage("")}>
                            {errorMessage}
                        </Alert>
                    </Snackbar>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        </LocalizationProvider>
    );
};

export default VendorRMWiseReport;